import {
  create_engagement_form,
  create_finding_form,
  create_request_form,
  finding_status_class,
  request_status_class
} from './audits_data';

function next_request_number(request_rows) {
  const next_number = request_rows.reduce((max, row) => {
    const match = row.ref.match(/RFI-(\d+)/);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0) + 1;
  return next_number;
}

function next_finding_number(findings_rows) {
  const next_number = findings_rows.reduce((max, row) => {
    const match = row.ref.match(/AFS-\d{4}-(\d+)/);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0) + 1;
  return next_number;
}

function fallback_text(value, fallback_value) {
  const trimmed_value = String(value || '').trim();
  return trimmed_value || fallback_value;
}

function build_request_status(status) {
  const request_status = { label: status, class: request_status_class(status) };
  return request_status;
}

function request_row_from_form(form, request_ref) {
  const request_row = {
    ref: request_ref,
    requested: fallback_text(form.requested, 'New evidence request'),
    audit_ref: form.audit_ref,
    area: fallback_text(form.area, 'Audit area to confirm'),
    requested_by: fallback_text(form.requested_by, 'AGSA'),
    owner: fallback_text(form.owner, 'Owner to assign'),
    due: fallback_text(form.due, 'TBC'),
    status: build_request_status(form.status),
    note: fallback_text(form.note, 'Request added.')
  };
  return request_row;
}

function update_request_row(existing, form) {
  const updated_request_row = {
    ...existing,
    requested: fallback_text(form.requested, existing.requested),
    audit_ref: form.audit_ref,
    area: fallback_text(form.area, existing.area),
    requested_by: fallback_text(form.requested_by, existing.requested_by),
    owner: fallback_text(form.owner, existing.owner),
    due: fallback_text(form.due, existing.due),
    status: build_request_status(form.status),
    note: fallback_text(form.note, existing.note)
  };
  return updated_request_row;
}

function build_finding_status(status) {
  const finding_status = { label: status, class: finding_status_class(status) };
  return finding_status;
}

function resolve_finding_title(form) {
  const title = fallback_text(form.title, '');
  const finding = fallback_text(form.finding, '');
  return title || finding || 'New audit finding';
}

function finding_row_from_form(form, finding_ref) {
  const finding_row = {
    ref: finding_ref,
    finding: resolve_finding_title(form),
    category: form.category,
    owner: fallback_text(form.owner, 'Owner to assign'),
    due: fallback_text(form.due, 'TBC'),
    rating: form.rating,
    status: build_finding_status(form.status),
    repeat: form.repeat === 'Yes'
  };
  return finding_row;
}

function engagement_status_details(readiness) {
  if (readiness >= 75) {
    const ready_status = { status: 'Ready', status_class: 'bg-success-subtle text-success-emphasis' };
    return ready_status;
  }
  const gap_status = { status: 'Gap', status_class: 'bg-warning-subtle text-warning-emphasis' };
  return gap_status;
}

function engagement_topics(scope_areas) {
  const topics = scope_areas.split('\n').map((item) => item.trim()).filter(Boolean).slice(0, 3);
  return topics.length ? topics : ['Scope to be confirmed'];
}

function resolve_engagement_owner(form) {
  const auditor = fallback_text(form.auditor, '');
  const lead_contact = fallback_text(form.lead_contact, '');
  return auditor || lead_contact || 'Audit owner to be assigned';
}

function resolve_engagement_period(form) {
  const fieldwork_dates = fallback_text(form.fieldwork_dates, '');
  const period = fallback_text(form.period, '');
  return fieldwork_dates || period || 'Planned - dates to confirm';
}

function engagement_row_from_form(form, engagements) {
  const title = fallback_text(form.name, 'New audit engagement');
  const owner = resolve_engagement_owner(form);
  const period = resolve_engagement_period(form);
  const readiness = Number(form.readiness) || 0;
  const status_details = engagement_status_details(readiness);
  const engagement_row = {
    code: `AUD-${String(engagements.length + 1).padStart(2, '0')}`,
    source: form.type,
    stage: form.status,
    title,
    owner,
    period,
    readiness,
    status: status_details.status,
    status_class: status_details.status_class,
    topics: engagement_topics(form.scope_areas)
  };
  return engagement_row;
}

export const audit_computed = {
  sections_ready_count() {
    return this.selected_audit_pack_sections.filter((section) => section.state === 'Ready').length;
  },
  selected_audit_pack_engagement() {
    return this.engagements.find((engagement) => engagement.code === this.selected_audit_pack_ref) || this.engagements[0] || null;
  },
  selected_audit_pack_sections() {
    return this.audit_pack_catalog[this.selected_audit_pack_ref]?.sections || [];
  },
  selected_scope_rows() {
    return this.audit_pack_catalog[this.selected_audit_pack_ref]?.scope_rows || [];
  },
  is_editing_request() {
    return Boolean(this.editing_request_ref);
  },
  finding_modal_selected_engagement() {
    return this.engagements.find((engagement) => engagement.code === this.finding_form.audit_ref) || null;
  }
};

export const audit_methods = {
  set_tab(tab_id) {
    this.active_tab = tab_id;
  },
  open_new_engagement_modal() {
    this.new_engagement_form = create_engagement_form();
    this.show_new_engagement_modal = true;
    document.body.classList.add('audit-modal-open');
  },
  close_new_engagement_modal() {
    this.show_new_engagement_modal = false;
    document.body.classList.remove('audit-modal-open');
  },
  open_new_request_modal() {
    this.editing_request_ref = null;
    this.request_form = create_request_form();
    this.show_request_modal = true;
    document.body.classList.add('audit-modal-open');
  },
  open_edit_request_modal(ref) {
    const row = this.request_rows.find((item) => item.ref === ref);
    if (!row) return;

    this.editing_request_ref = ref;
    this.request_form = {
      requested: row.requested,
      audit_ref: row.audit_ref || 'AUD-01',
      area: row.area,
      requested_by: row.requested_by,
      owner: row.owner,
      due: row.due,
      status: row.status.label,
      note: row.note
    };
    this.show_request_modal = true;
    document.body.classList.add('audit-modal-open');
  },
  close_request_modal() {
    this.show_request_modal = false;
    this.editing_request_ref = null;
    this.request_form = create_request_form();
    document.body.classList.remove('audit-modal-open');
  },
  open_new_finding_modal() {
    this.finding_form = create_finding_form();
    this.show_finding_modal = true;
    document.body.classList.add('audit-modal-open');
  },
  close_finding_modal() {
    this.show_finding_modal = false;
    this.finding_form = create_finding_form();
    document.body.classList.remove('audit-modal-open');
  },
  create_request() {
    const form = this.request_form;
    const nextNumber = next_request_number(this.request_rows);
    const request_ref = `RFI-${String(nextNumber).padStart(3, '0')}`;
    this.request_rows.unshift(request_row_from_form(form, request_ref));

    this.close_request_modal();
  },
  save_request() {
    const index = this.request_rows.findIndex((item) => item.ref === this.editing_request_ref);
    if (index === -1) return;

    const existing = Reflect.get(this.request_rows, index);
    const updated_request = update_request_row(existing, this.request_form);
    this.request_rows.splice(index, 1, updated_request);

    this.close_request_modal();
  },
  delete_request() {
    if (!this.editing_request_ref) return;
    this.request_rows = this.request_rows.filter((item) => item.ref !== this.editing_request_ref);
    this.close_request_modal();
  },
  create_finding() {
    const form = this.finding_form;
    const nextNumber = next_finding_number(this.findings_rows);
    const yearMatch = (form.due || '').match(/\b(\d{4})\b/);
    const year = yearMatch ? yearMatch[1] : '2026';
    const finding_ref = `AFS-${year}-${String(nextNumber).padStart(3, '0')}`;
    this.findings_rows.unshift(finding_row_from_form(form, finding_ref));

    this.close_finding_modal();
  },
  create_engagement() {
    const form = this.new_engagement_form;
    this.engagements.unshift(engagement_row_from_form(form, this.engagements));

    this.close_new_engagement_modal();
  }
};
